import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Download, Sparkles, X, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dismissKeyFor = (slug) => `bestbuddy_anniv_dismissed_${slug || 'pending'}`;

// Lightweight CSS confetti — 30 floating emoji particles
const ConfettiBurst = () => {
    const particles = Array.from({ length: 24 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 1.5,
        emoji: ['🎉', '✨', '🌟', '🎊', '🐺', '🥇'][i % 6],
    }));
    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            {particles.map((p) => (
                <span
                    key={p.id}
                    style={{
                        position: 'absolute',
                        left: `${p.left}%`,
                        top: '-10%',
                        animation: `anniv-fall ${p.duration}s linear ${p.delay}s 1`,
                        fontSize: 18,
                    }}
                >
                    {p.emoji}
                </span>
            ))}
        </div>
    );
};

export default function AnniversaryBanner({ preview = false }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const url = preview ? `${API}/anniversary/status?force=true` : `${API}/anniversary/status`;
            const res = await axios.get(url);
            setStatus(res.data);
            const key = dismissKeyFor(res.data?.badge_slug);
            if (localStorage.getItem(key)) setDismissed(true);
        } catch (e) {
            // silent fail — banner is non-critical
        } finally {
            setLoading(false);
        }
    }, [preview]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const handleClaim = async () => {
        setClaiming(true);
        try {
            const res = await axios.post(`${API}/anniversary/claim-badge`, null);
            toast.success(`🏅 Badge "${res.data.badge_slug}" eingesammelt!`);
            setStatus((s) => ({ ...s, badge_claimed: true }));
        } catch (e) {
            toast.error('Badge konnte nicht eingesammelt werden.');
        } finally {
            setClaiming(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API}/anniversary/pdf?language=de`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `nanouk_anniversary_year${status?.years_with_nanouk || 1}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('🎁 Dein Jahres-PDF wurde geladen!');
        } catch (e) {
            toast.error('Download fehlgeschlagen.');
        } finally {
            setDownloading(false);
        }
    };

    const handleDismiss = () => {
        if (status?.badge_slug) localStorage.setItem(dismissKeyFor(status.badge_slug), String(Date.now()));
        setDismissed(true);
    };

    if (loading || !status || !status.is_anniversary || dismissed) return null;

    const years = status.years_with_nanouk || 1;

    return (
        <AnimatePresence>
            <motion.div
                data-testid="anniversary-banner"
                initial={{ opacity: 0, y: -10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="relative overflow-hidden rounded-2xl border-2 border-amber-300/60 dark:border-amber-500/40 bg-gradient-to-br from-amber-50 via-orange-50 to-purple-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-purple-950/30 shadow-lg"
            >
                <style>{`@keyframes anniv-fall { from { transform: translateY(0) rotate(0deg); opacity: 1; } to { transform: translateY(220px) rotate(360deg); opacity: 0; } }`}</style>
                <ConfettiBurst />

                <button
                    onClick={handleDismiss}
                    aria-label="Dismiss anniversary banner"
                    data-testid="anniversary-dismiss-btn"
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-10"
                >
                    <X className="w-4 h-4 text-gray-500" />
                </button>

                <div className="relative p-5 md:p-6 flex flex-col md:flex-row items-center gap-4 md:gap-6">
                    {/* Medal */}
                    <div className="flex-shrink-0 relative">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-orange-500 flex items-center justify-center shadow-xl ring-4 ring-white/60 dark:ring-amber-300/30">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-amber-100 to-yellow-200 flex flex-col items-center justify-center">
                                <span className="text-2xl md:text-3xl font-black text-amber-700">{years}</span>
                                <span className="text-[9px] md:text-[10px] font-bold text-amber-800 uppercase tracking-wider">Jahr{years > 1 ? 'e' : ''}</span>
                            </div>
                        </div>
                        <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-500 animate-pulse" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-center md:text-left">
                        <h3 className="text-xl md:text-2xl font-bold text-amber-900 dark:text-amber-100 flex flex-wrap items-center gap-2 justify-center md:justify-start">
                            🎉 {years === 1 ? '1 Jahr Nanouk!' : `${years} Jahre mit Nanouk!`}
                            {status.preview && (
                                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">Vorschau</span>
                            )}
                        </h3>
                        <p className="text-sm md:text-base text-amber-800/80 dark:text-amber-200/80 mt-1">
                            Dein persönliches Jahres-Briefing wartet — mit allen Highlights, einem Brief von Nanouk und deinem limited-edition Badge 🥇
                        </p>

                        <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-center md:justify-start">
                            <button
                                onClick={handleDownload}
                                disabled={downloading}
                                data-testid="anniversary-download-btn"
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Download className="w-4 h-4" />
                                {downloading ? 'Lädt…' : 'Mein Jahres-PDF öffnen'}
                            </button>

                            {!status.badge_claimed ? (
                                <button
                                    onClick={handleClaim}
                                    disabled={claiming}
                                    data-testid="anniversary-claim-btn"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-white/10 border border-amber-300 text-amber-900 dark:text-amber-100 font-semibold text-sm hover:bg-white transition-all disabled:opacity-60"
                                >
                                    <Gift className="w-4 h-4" />
                                    {claiming ? 'Sammle…' : 'Badge einsammeln'}
                                </button>
                            ) : (
                                <span
                                    data-testid="anniversary-claimed-pill"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 font-semibold text-sm"
                                >
                                    <Award className="w-4 h-4" /> Badge eingesammelt ✨
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
